import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, MessagesSquare, UserCheck, UserMinus, Shuffle, ShieldAlert, Search, CheckCircle2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChatThread } from "@/components/chat/ChatThread";
import { ChatComposer } from "@/components/chat/ChatComposer";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, useApi } from "@/lib/api";
import { uploadChat, fmtClock } from "@/lib/chat";
import { ROLE_LABEL, rank } from "@/lib/format";
import { TemplateManager } from "@/admin/TemplateManager";
import { ServiceBar } from "@/components/chat/ServiceBar";
import { HolidayManager } from "@/admin/HolidayManager";

const FILTERS = [["all", "Semua"], ["mine", "Milik Saya"], ["open", "Belum Diambil"]];

const ThreadList = ({ threads, active, onPick, me }) => {
  const [f, setF] = useState("all");
  const [q, setQ] = useState("");
  const list = (threads || []).filter((t) => (f === "mine" ? t.assigned_to === me : f === "open" ? !t.assigned_to : true))
    .filter((t) => !q || `${t.member_name} ${t.member_no}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="flex flex-col h-full">
      <div className="p-3 space-y-2 border-b border-slate-100">
        <div className="flex items-center gap-2 bg-slate-100 rounded-xl px-3 h-10"><Search className="h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari anggota" className="bg-transparent outline-none text-sm flex-1" data-testid="chat-search" />
        </div>
        <div className="flex gap-1.5">
          {FILTERS.map(([k, l]) => (
            <button key={k} onClick={() => setF(k)} data-testid={`chat-filter-${k}`}
              className={`px-3 py-1 rounded-full text-xs font-semibold ${f === k ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto" data-testid="chat-thread-list">
        {list.length === 0 && <p className="text-sm text-center text-slate-500 py-10">Belum ada chat</p>}
        {list.map((t) => (
          <button key={t.thread_id} onClick={() => onPick(t.thread_id)} data-testid={`chat-thread-${t.thread_id}`}
            className={`w-full text-left flex items-center gap-3 px-3 py-3 border-b border-slate-50 hover:bg-slate-50 ${active === t.thread_id ? "bg-emerald-50" : ""}`}>
            <span className="h-11 w-11 rounded-full bg-gradient-to-br from-blue-600 to-sky-400 text-white flex items-center justify-center font-bold shrink-0">{(t.member_name || "?")[0].toUpperCase()}</span>
            <span className="flex-1 min-w-0">
              <span className="flex justify-between gap-2"><span className="font-semibold text-sm truncate">{t.member_name}</span><span className={`text-[11px] ${t.unread ? "text-emerald-600 font-bold" : "text-slate-400"}`}>{fmtClock(t.last_at)}</span></span>
              <span className="flex justify-between gap-2 items-center">
                <span className="text-xs text-slate-500 truncate">{t.last_message}</span>
                {t.unread > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-600 text-white text-[11px] font-bold flex items-center justify-center" data-testid={`chat-thread-unread-${t.thread_id}`}>{t.unread}</span>}
              </span>
              <span className={`text-[10px] font-semibold ${t.status === "closed" ? "text-slate-500" : t.assigned_to ? "text-blue-700" : "text-amber-600"}`}>{t.status === "closed" ? `Selesai${t.last_rating && !t.rating_pending ? ` • ${t.last_rating}★` : ""}` : t.assigned_to ? `Ditangani ${t.assigned_name}` : "Belum diambil"}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const AssignBar = ({ t, me, isSuper, staff, act }) => {
  const mine = t.assigned_to === me;
  const other = t.assigned_to && !mine;
  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 bg-white border-b border-slate-100 text-xs" data-testid="chat-assign-bar">
      <span className="font-semibold text-slate-600 mr-auto" data-testid="chat-assigned-label">{t.assigned_to ? `Ditangani: ${t.assigned_name}${mine ? " (Anda)" : ""}` : "Belum ada petugas"}</span>
      {!t.assigned_to && <button onClick={() => act("claim")} data-testid="chat-claim" className="flex items-center gap-1 bg-emerald-600 text-white px-3 py-1.5 rounded-lg font-semibold"><UserCheck className="h-3.5 w-3.5" />Ambil</button>}
      {other && isSuper && <button onClick={() => act("claim")} data-testid="chat-takeover" className="flex items-center gap-1 bg-rose-600 text-white px-3 py-1.5 rounded-lg font-semibold"><ShieldAlert className="h-3.5 w-3.5" />Ambil Alih</button>}
      {(mine || (other && isSuper)) && <button onClick={() => act("release")} data-testid="chat-release" className="flex items-center gap-1 border border-slate-200 px-3 py-1.5 rounded-lg font-semibold"><UserMinus className="h-3.5 w-3.5" />Lepas</button>}
      {t.status !== "closed" && (!other || isSuper) && <button onClick={() => act("close")} data-testid="chat-close" className="flex items-center gap-1 bg-blue-700 text-white px-3 py-1.5 rounded-lg font-semibold"><CheckCircle2 className="h-3.5 w-3.5" />Selesai</button>}
      {t.status === "closed" && <span className="text-[11px] font-bold text-blue-700 bg-blue-50 rounded-full px-2.5 py-1" data-testid="chat-closed-badge">Selesai{t.last_rating && !t.rating_pending ? ` • ${"★".repeat(t.last_rating)}` : " • menunggu rating"}</span>}
      {(!other || isSuper) && (
        <Select value="" onValueChange={(v) => act("transfer", { to_user_id: v })}>
          <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="chat-transfer-select"><Shuffle className="h-3.5 w-3.5 mr-1" /><SelectValue placeholder="Alihkan ke..." /></SelectTrigger>
          <SelectContent>{staff?.filter((s) => s.user_id !== t.assigned_to).map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.name} • {s.position || ROLE_LABEL[s.role]}</SelectItem>)}</SelectContent>
        </Select>
      )}
    </div>
  );
};

export default function AdminChat() {
  const { user } = useAuth();
  const isSuper = user.role === "superadmin";
  const [threads, setThreads] = useState(null);
  const [active, setActive] = useState(null);
  const [conv, setConv] = useState(null);
  const { data: staff } = useApi("/chat-admin/staff");
  const { data: templates, reload: reloadTpl } = useApi("/chat-admin/templates");
  const [tplOpen, setTplOpen] = useState(false);

  const loadThreads = useCallback(() => api.get("/chat-admin/threads").then((r) => setThreads(r.data)).catch(() => {}), []);
  const loadConv = useCallback(() => active && api.get(`/chat-admin/threads/${active}/messages`).then((r) => {
    setConv(r.data);
    window.dispatchEvent(new Event("cu-chat-read"));
  }).catch(() => {}), [active]);

  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 5000);
    return () => clearInterval(t);
  }, [loadThreads]);
  useEffect(() => {
    setConv(null);
    loadConv();
    const t = setInterval(loadConv, 3000);
    return () => clearInterval(t);
  }, [loadConv]);

  const wrap = (fn) => async (...a) => {
    try {
      await fn(...a);
      await Promise.all([loadConv(), loadThreads()]);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const act = wrap((kind, body) => api.post(`/chat-admin/threads/${active}/${kind}`, body));
  const t = conv?.thread;
  const blocked = t?.assigned_to && t.assigned_to !== user.user_id && !isSuper ? `Chat ini sedang ditangani ${t.assigned_name}. Minta dilepas atau dialihkan untuk membalas.` : null;
  const base = `/chat-admin/threads/${active}`;

  return (
    <div data-testid="admin-chat-page">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-11 w-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-700 text-white flex items-center justify-center"><MessagesSquare className="h-5 w-5" /></span>
        <div className="mr-auto">
          <h1 className="text-2xl font-bold text-slate-900">Chat Admin</h1>
          <p className="text-sm text-slate-500">Balas kendala anggota. Ambil, lepas, atau alihkan chat ke petugas lain.</p>
        </div>
        <HolidayManager canEdit={rank(user) >= 2} />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden grid md:grid-cols-[320px_1fr] h-[calc(100dvh-260px)] min-h-[480px]">
        <div className={`md:border-r border-slate-100 ${active ? "hidden md:block" : ""}`}>
          <ThreadList threads={threads} active={active} onPick={setActive} me={user.user_id} />
        </div>
        <div className={`flex-col min-h-0 ${active ? "flex" : "hidden md:flex"}`}>
          {!active && <div className="flex-1 flex items-center justify-center text-sm text-slate-500" data-testid="chat-pick-hint">Pilih chat anggota di sebelah kiri</div>}
          {active && (
            <>
              <div className="flex items-center gap-3 px-3 py-2.5 bg-emerald-700 text-white">
                <button className="md:hidden" onClick={() => setActive(null)} data-testid="admin-chat-back" aria-label="Kembali"><ArrowLeft className="h-5 w-5" /></button>
                <div className="leading-tight">
                  <div className="font-semibold" data-testid="admin-chat-member-name">{t?.member_name || "..."}</div>
                  <div className="text-[11px] text-emerald-100">{t?.member_no}</div>
                </div>
              </div>
              {t && <AssignBar t={t} me={user.user_id} isSuper={isSuper} staff={staff} act={act} />}
              <ServiceBar service={conv?.service} />}
              <ChatThread messages={conv?.messages} viewer="admin" emptyText="Belum ada pesan" />
              <ChatComposer blockedNote={blocked}
                quickReplies={templates || []} memberName={t?.member_name}
                onManageTemplates={rank(user) >= 2 ? () => setTplOpen(true) : undefined}
                onText={wrap((text) => api.post(`${base}/messages`, { text }))}
                onFile={wrap((f, d) => uploadChat(`${base}/upload`, f, d))}
                onLocation={wrap((loc) => api.post(`${base}/messages`, loc))} />
            </>
          )}
        </div>
      </div>
      <TemplateManager open={tplOpen} onOpenChange={setTplOpen} templates={templates} reload={reloadTpl} />
    </div>
  );
}
